import { type FC, useCallback, useState } from "react";
import { Platform, Pressable, View, type ViewStyle } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../../theme";
import { useCombobox } from "../../../hooks/use-combobox";
import { Icon } from "../../Icon";
import { Text } from "../../Text";
import type { PaginationMenuPlacement } from "../Pagination.types";
import { CONTROL_SIZE } from "../pagination-metrics";

export interface PaginationPageSizeSelectProps {
  readonly value: number;
  readonly options: readonly number[];
  readonly onChange: (pageSize: number) => void;
  readonly disabled: boolean;
  readonly accessibilityLabel: string;
  /** @default "bottom" */
  readonly placement?: PaginationMenuPlacement | undefined;
}

const getOptionId = (option: number): string => option.toString();

/**
 * The page-size trigger and its menu.
 *
 * A menu rather than a stepper or a text field: the sizes on offer are a fixed
 * short list, and typing a number would invite values the backing query cannot
 * serve.
 */
const PaginationPageSizeSelect: FC<PaginationPageSizeSelectProps> = ({
  value,
  options,
  onChange,
  disabled,
  accessibilityLabel,
  placement = "bottom",
}) => {
  const { theme } = useUnistyles();
  const [hovered, setHovered] = useState(false);

  const { isOpen, open, close, containerRef, highlightedIndex, setHighlightedIndex, getOptionProps } =
    useCombobox<number>({
      options,
      getOptionId,
      onSelect: onChange,
    });

  const handleTriggerPress = useCallback(() => {
    if (isOpen) {
      close();
      return;
    }
    open();
  }, [isOpen, open, close]);

  return (
    <View ref={containerRef} style={[styles.container, isOpen && styles.containerOpen]}>
      <Pressable
        onPress={handleTriggerPress}
        disabled={disabled}
        onHoverIn={() => {
          setHovered(true);
        }}
        onHoverOut={() => {
          setHovered(false);
        }}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ disabled, expanded: isOpen }}
        style={({ pressed }) => [
          styles.trigger,
          hovered && !disabled && styles.triggerHovered,
          (pressed || isOpen) && !disabled && styles.triggerActive,
          disabled && styles.triggerDisabled,
          Platform.OS === "web" && !disabled && styles.webInteractive,
        ]}
      >
        <Text textStyle="sm/medium">{value.toString()}</Text>
        <Icon name={isOpen ? "chevronUp" : "chevronDown"} size={14} color={theme.colors.icon} />
      </Pressable>

      {isOpen && (
        <>
          {/* Native has no global hit-test, so the tap-away target has to exist
              in the tree. On web `useCombobox` handles it on the document. */}
          {Platform.OS !== "web" && <Pressable style={styles.backdrop} onPress={close} />}
          <View
            style={[styles.menu, placement === "top" ? styles.menuAbove : styles.menuBelow]}
            accessibilityRole="menu"
          >
            {options.map((option, index) => {
              const optionProps = getOptionProps(option, index);
              const selected = option === value;

              return (
                <Pressable
                  key={getOptionId(option)}
                  onPress={optionProps.onPress}
                  onHoverIn={() => {
                    setHighlightedIndex(index);
                  }}
                  accessibilityRole="menuitem"
                  accessibilityState={{ selected }}
                  style={[
                    styles.option,
                    index === highlightedIndex && styles.optionHighlighted,
                    selected && styles.optionSelected,
                    Platform.OS === "web" && styles.webInteractive,
                  ]}
                >
                  <Text textStyle={selected ? "sm/semibold" : "sm/regular"} color={selected ? "text" : "textMuted"}>
                    {getOptionId(option)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </>
      )}
    </View>
  );
};

PaginationPageSizeSelect.displayName = "PaginationPageSizeSelect";

export { PaginationPageSizeSelect };

const MENU_OFFSET = 4;

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "relative",
  },
  containerOpen: {
    zIndex: theme.zIndex.inputOpen,
  },
  trigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xs,
    height: CONTROL_SIZE,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.sm,
    borderRadius: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    backgroundColor: theme.colors.surface,
  },
  triggerHovered: {
    borderColor: theme.colors.field.border_default,
  },
  triggerActive: {
    borderColor: theme.colors.interactive.buttons.default_background,
  },
  triggerDisabled: {
    opacity: 0.5,
  },
  backdrop: {
    position: "absolute",
    top: -1000,
    left: -1000,
    right: -1000,
    bottom: -1000,
    zIndex: theme.zIndex.backdrop,
  },
  menu: {
    position: "absolute",
    left: 0,
    minWidth: "100%",
    paddingVertical: theme.spacing.xs,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.md,
    // Options run edge-to-edge with no horizontal inset, so their square
    // hover/selected background clips to the container's rounded corners
    // instead of overflowing past them. Doesn't affect the shadow below —
    // box-shadow draws outside the border box, unaffected by this element's
    // own overflow.
    overflow: "hidden",
    ...theme.shadows.sm,
    zIndex: theme.zIndex.dropdown,
  },
  menuAbove: {
    bottom: CONTROL_SIZE + MENU_OFFSET,
  },
  menuBelow: {
    top: CONTROL_SIZE + MENU_OFFSET,
  },
  option: {
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xl,
    alignItems: "flex-start",
  },
  optionHighlighted: {
    backgroundColor: theme.colors.surface2.hover,
  },
  optionSelected: {
    backgroundColor: theme.colors.surface2.selected,
  },
  webInteractive: Object.assign({ cursor: "pointer" } satisfies ViewStyle, {
    transition: "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
  }),
}));
